import * as React from "react";
import { Dialog as SheetPrimitive } from "@base-ui/react/dialog";
import { XIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "src/shadcn/lib/utils";
import { Button } from "src/shadcn/components/button";

import {
  Sheet as ShadcnSheet,
  SheetTrigger as ShadcnSheetTrigger,
  SheetClose as ShadcnSheetClose,
  SheetHeader as ShadcnSheetHeader,
  SheetFooter as ShadcnSheetFooter,
  SheetTitle as ShadcnSheetTitle,
  SheetDescription as ShadcnSheetDescription,
} from "src/shadcn/components/sheet";

const Sheet = (props: React.ComponentProps<typeof ShadcnSheet>) => (
  <ShadcnSheet {...props} />
);

const SheetTrigger = (
  props: React.ComponentProps<typeof ShadcnSheetTrigger>
) => <ShadcnSheetTrigger {...props} />;

const SheetClose = (props: React.ComponentProps<typeof ShadcnSheetClose>) => (
  <ShadcnSheetClose {...props} />
);

/**
 * SheetContent composes Portal + Backdrop + Base UI Popup directly so
 * the overlay class and width stay fully consumer-controlled.
 */
const SheetContent = ({
  className,
  children,
  side = "right",
  showCloseButton = true,
  overlayClassName,
  keepMounted,
  ...props
}: SheetPrimitive.Popup.Props & {
  side?: "top" | "right" | "bottom" | "left";
  showCloseButton?: boolean;
  overlayClassName?: string;
  keepMounted?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <SheetPrimitive.Portal data-slot="sheet-portal" keepMounted={keepMounted}>
      <SheetPrimitive.Backdrop
        forceRender
        data-slot="sheet-overlay"
        className={cn(
          "fixed inset-0 z-50 bg-black/50 transition-opacity duration-150 data-ending-style:opacity-0 data-starting-style:opacity-0",
          overlayClassName
        )}
      />
      <SheetPrimitive.Popup
        data-slot="sheet-content"
        data-side={side}
        className={cn(
          "fixed z-50 flex flex-col gap-4 bg-popover bg-clip-padding text-sm text-popover-foreground shadow-lg outline-none transition duration-200 ease-in-out",
          "data-[side=bottom]:inset-x-0 data-[side=bottom]:bottom-0 data-[side=bottom]:h-auto data-[side=bottom]:border-t data-[side=bottom]:border-border",
          "data-[side=left]:inset-y-0 data-[side=left]:left-0 data-[side=left]:h-full data-[side=left]:w-3/4 data-[side=left]:border-e data-[side=left]:border-border data-[side=left]:sm:max-w-sm",
          "data-[side=right]:inset-y-0 data-[side=right]:right-0 data-[side=right]:h-full data-[side=right]:w-3/4 data-[side=right]:border-s data-[side=right]:border-border data-[side=right]:sm:max-w-sm",
          "data-[side=top]:inset-x-0 data-[side=top]:top-0 data-[side=top]:h-auto data-[side=top]:border-b data-[side=top]:border-border",
          "data-starting-style:opacity-0 data-ending-style:opacity-0",
          "data-[side=bottom]:data-starting-style:translate-y-[2.5rem] data-[side=bottom]:data-ending-style:translate-y-[2.5rem]",
          "data-[side=left]:data-starting-style:translate-x-[-2.5rem] data-[side=left]:data-ending-style:translate-x-[-2.5rem]",
          "data-[side=right]:data-starting-style:translate-x-[2.5rem] data-[side=right]:data-ending-style:translate-x-[2.5rem]",
          "data-[side=top]:data-starting-style:translate-y-[-2.5rem] data-[side=top]:data-ending-style:translate-y-[-2.5rem]",
          className
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <SheetPrimitive.Close
            data-slot="sheet-close"
            render={
              <Button
                variant="ghost"
                className="absolute top-3 end-3"
                size="icon-sm"
              />
            }
          >
            <XIcon />
            <span className="sr-only">
              {t("neetoatoms.sheet.close", "Close")}
            </span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Popup>
    </SheetPrimitive.Portal>
  );
};

const SheetHeader = ({
  className,
  ...props
}: React.ComponentProps<typeof ShadcnSheetHeader>) => (
  <ShadcnSheetHeader className={cn("gap-3 p-6", className)} {...props} />
);

const SheetFooter = ({
  className,
  ...props
}: React.ComponentProps<typeof ShadcnSheetFooter>) => (
  <ShadcnSheetFooter
    className={cn("bg-muted/50 px-6 py-4", className)}
    {...props}
  />
);

const SheetTitle = (props: React.ComponentProps<typeof ShadcnSheetTitle>) => (
  <ShadcnSheetTitle {...props} />
);

const SheetDescription = (
  props: React.ComponentProps<typeof ShadcnSheetDescription>
) => <ShadcnSheetDescription {...props} />;

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
